// Reportes v1: por persona/semana, por tarjeta (estimado vs. real), por Etiqueta y por Marca.
// La fuente de verdad son las funciones SQL (public.report_*); este módulo define:
// - el orden y el formato de las filas (tabla en pantalla y CSV salen del mismo modelo, así
//   el CSV es idéntico a lo mostrado);
// - una agregación de referencia en TypeScript, con la misma semántica que el SQL, que usan
//   las pruebas para verificar los totales.

import {
  roundMinutes,
  minutesToHours,
  formatMinutes,
  formatHours,
  type Rounding,
} from './rounding.ts';
import { weekStartOfDate } from './time.ts';

export type ReportKind = 'persona_semana' | 'tarjeta' | 'etiqueta' | 'marca';

export const REPORT_KINDS: readonly { kind: ReportKind; label: string }[] = [
  { kind: 'persona_semana', label: 'Por persona y semana' },
  { kind: 'tarjeta', label: 'Por tarjeta' },
  { kind: 'etiqueta', label: 'Por etiqueta' },
  { kind: 'marca', label: 'Por marca' },
];

export const NO_ETIQUETA = '(sin etiqueta)';
export const NO_MARCA = '(sin marca)';
export const UNKNOWN_CARD = '(tarjeta no disponible)';

export interface PersonWeekRow {
  user_id: string;
  person: string;
  week_start: string;
  total_seconds: number;
  minutes: number;
}
export interface CardReportRow {
  card_id: string;
  card_name: string;
  status: string | null;
  horas_estimadas: number | null;
  total_seconds: number;
  minutes: number;
  horas_reales: number;
  diferencia: number | null;
}
export interface TagReportRow {
  etiqueta: string;
  total_seconds: number;
  minutes: number;
}
export interface BrandReportRow {
  marca: string;
  total_seconds: number;
  minutes: number;
}

export interface ReportRowsByKind {
  persona_semana: PersonWeekRow;
  tarjeta: CardReportRow;
  etiqueta: TagReportRow;
  marca: BrandReportRow;
}

/** Un registro de work_logs: persona, tarjeta, día local y segundos trabajados. */
export interface ReportEntry {
  user_id: string;
  card_id: string;
  work_date: string;
  seconds: number;
  deleted_at: string | null;
}
export interface ReportCard {
  id: string;
  name: string;
  status: string | null;
  etiquetas: readonly string[];
  marca: string | null;
  horas_estimadas: number | null;
}
export interface ReportPerson {
  id: string;
  name: string;
}
/** Fechas locales (America/Santiago) inclusivas. */
export interface DateRange {
  from: string;
  to: string;
}

const collator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });
const round2 = (n: number) => Math.round(n * 100) / 100;

const entrySeconds = (entry: ReportEntry) => entry.seconds;

/** Un registro cuenta si no está borrado y su día cae en el rango. */
export function entryInRange(entry: ReportEntry, range: DateRange): boolean {
  if (entry.deleted_at) return false;
  return entry.work_date >= range.from && entry.work_date <= range.to;
}

export function sortReportRows<K extends ReportKind>(
  kind: K,
  rows: ReportRowsByKind[K][],
): ReportRowsByKind[K][] {
  const list = [...rows];
  switch (kind) {
    case 'persona_semana':
      return (list as PersonWeekRow[]).sort(
        (a, b) => a.week_start.localeCompare(b.week_start) || collator.compare(a.person, b.person),
      ) as ReportRowsByKind[K][];
    case 'tarjeta':
      return (list as CardReportRow[]).sort(
        (a, b) => b.total_seconds - a.total_seconds || collator.compare(a.card_name, b.card_name),
      ) as ReportRowsByKind[K][];
    case 'etiqueta':
      return (list as TagReportRow[]).sort(
        (a, b) => b.total_seconds - a.total_seconds || collator.compare(a.etiqueta, b.etiqueta),
      ) as ReportRowsByKind[K][];
    default:
      return (list as BrandReportRow[]).sort(
        (a, b) => b.total_seconds - a.total_seconds || collator.compare(a.marca, b.marca),
      ) as ReportRowsByKind[K][];
  }
}

function sumBy<T>(
  items: T[],
  key: (t: T) => string,
  seconds: (t: T) => number,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const item of items) map.set(key(item), (map.get(key(item)) ?? 0) + seconds(item));
  return map;
}

/** Agregación de referencia (misma semántica que las funciones SQL). */
export function aggregateReport<K extends ReportKind>(
  kind: K,
  input: {
    entries: readonly ReportEntry[];
    cards: readonly ReportCard[];
    people: readonly ReportPerson[];
    range: DateRange;
    rounding: Rounding;
  },
): ReportRowsByKind[K][] {
  const entries = input.entries.filter((e) => entryInRange(e, input.range));
  const cards = new Map(input.cards.map((c) => [c.id, c]));
  const people = new Map(input.people.map((p) => [p.id, p.name]));
  const r = input.rounding;

  if (kind === 'persona_semana') {
    const sums = sumBy(
      entries,
      (e) => `${e.user_id}|${weekStartOfDate(e.work_date)}`,
      entrySeconds,
    );
    const rows: PersonWeekRow[] = [...sums].map(([key, secs]) => {
      const [userId = '', week = ''] = key.split('|');
      return {
        user_id: userId,
        person: people.get(userId) ?? userId,
        week_start: week,
        total_seconds: secs,
        minutes: roundMinutes(secs, r),
      };
    });
    return sortReportRows(kind, rows as ReportRowsByKind[K][]);
  }
  if (kind === 'tarjeta') {
    const sums = sumBy(entries, (e) => e.card_id, entrySeconds);
    const rows: CardReportRow[] = [...sums].map(([cardId, secs]) => {
      const card = cards.get(cardId);
      const minutes = roundMinutes(secs, r);
      const reales = minutesToHours(minutes);
      const est = card?.horas_estimadas ?? null;
      return {
        card_id: cardId,
        card_name: card?.name ?? UNKNOWN_CARD,
        status: card?.status ?? null,
        horas_estimadas: est,
        total_seconds: secs,
        minutes,
        horas_reales: reales,
        diferencia: est === null ? null : round2(reales - est),
      };
    });
    return sortReportRows(kind, rows as ReportRowsByKind[K][]);
  }
  if (kind === 'etiqueta') {
    const sums = new Map<string, number>();
    for (const e of entries) {
      const tags = cards.get(e.card_id)?.etiquetas ?? [];
      for (const tag of tags.length > 0 ? new Set(tags) : [NO_ETIQUETA]) {
        sums.set(tag, (sums.get(tag) ?? 0) + entrySeconds(e));
      }
    }
    const rows: TagReportRow[] = [...sums].map(([etiqueta, secs]) => ({
      etiqueta,
      total_seconds: secs,
      minutes: roundMinutes(secs, r),
    }));
    return sortReportRows(kind, rows as ReportRowsByKind[K][]);
  }
  const sums = sumBy(entries, (e) => cards.get(e.card_id)?.marca ?? NO_MARCA, entrySeconds);
  const rows: BrandReportRow[] = [...sums].map(([marca, secs]) => ({
    marca,
    total_seconds: secs,
    minutes: roundMinutes(secs, r),
  }));
  return sortReportRows(kind, rows as ReportRowsByKind[K][]);
}

// ---------------------------------------------------------------------------
// Tabla (pantalla y CSV)
// ---------------------------------------------------------------------------

export interface ReportTable {
  headers: string[];
  rows: string[][];
  /** Fila de total (suma de minutos mostrados). */
  footer: string[];
}

function formatWeek(weekStartDate: string): string {
  const [y, m, d] = weekStartDate.split('-');
  return `${d}/${m}/${y}`;
}

export function reportTable<K extends ReportKind>(
  kind: K,
  rows: ReportRowsByKind[K][],
): ReportTable {
  const totalMinutes = rows.reduce((s, row) => s + row.minutes, 0);
  const total = formatMinutes(totalMinutes);
  const totalHours = formatHours(minutesToHours(totalMinutes));
  switch (kind) {
    case 'persona_semana':
      return {
        headers: ['Persona', 'Semana (lunes)', 'Tiempo (h:mm)', 'Horas'],
        rows: (rows as PersonWeekRow[]).map((row) => [
          row.person,
          formatWeek(row.week_start),
          formatMinutes(row.minutes),
          formatHours(minutesToHours(row.minutes)),
        ]),
        footer: ['Total', '', total, totalHours],
      };
    case 'tarjeta': {
      const list = rows as CardReportRow[];
      const est = list.reduce((s, row) => s + (row.horas_estimadas ?? 0), 0);
      return {
        headers: [
          'Tarjeta',
          'Status',
          'Horas estimadas',
          'Horas reales',
          'Diferencia',
          'Tiempo (h:mm)',
        ],
        rows: list.map((row) => [
          row.card_name,
          row.status ?? '',
          formatHours(row.horas_estimadas),
          formatHours(row.horas_reales),
          formatHours(row.diferencia),
          formatMinutes(row.minutes),
        ]),
        footer: ['Total', '', formatHours(round2(est)), totalHours, '', total],
      };
    }
    case 'etiqueta':
      return {
        headers: ['Etiqueta', 'Tiempo (h:mm)', 'Horas'],
        rows: (rows as TagReportRow[]).map((row) => [
          row.etiqueta,
          formatMinutes(row.minutes),
          formatHours(minutesToHours(row.minutes)),
        ]),
        footer: ['Total (una tarjeta con varias etiquetas suma en cada una)', total, totalHours],
      };
    default:
      return {
        headers: ['Marca', 'Tiempo (h:mm)', 'Horas'],
        rows: (rows as BrandReportRow[]).map((row) => [
          row.marca,
          formatMinutes(row.minutes),
          formatHours(minutesToHours(row.minutes)),
        ]),
        footer: ['Total', total, totalHours],
      };
  }
}

function csvCell(value: string): string {
  return /[";\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** CSV con separador ';' (Excel en español) y BOM UTF-8; mismas celdas que la tabla en pantalla. */
export function tableToCsv(table: ReportTable): string {
  const lines = [table.headers, ...table.rows, table.footer].map((row) =>
    row.map(csvCell).join(';'),
  );
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}
